<template>
  <Dialog v-model:open="open">
    <DialogContent>
      <DialogHeader><DialogTitle>Upload Image</DialogTitle></DialogHeader>
      <div class="px-7 py-6">
        <div v-if="src">
          <ImageCropper ref="cropper" :src="src" viewport-type="square" />
        </div>
        <label
          v-else
          id="drop-area"
          for="image-file"
          class="bg-black/50 w-[300px] h-[180px] cursor-pointer rounded-lg border-dashed border-2 flex flex-col justify-center items-center hover:border-white"
          :class="{ 'border-white': highlight, 'border-gray-300': !highlight }"
          @dragenter.prevent.stop="onDragEnter"
          @dragover.prevent.stop="onDragOver"
          @dragleave.prevent.stop="onDragLeave"
          @drop.prevent.stop="onDrop"
        >
          <SvgIcon icon="app-icons/upload-image" width="100" height="100" />
          <span>Drag your image or click to select</span>
        </label>
        <input
          id="image-file"
          class="hidden"
          type="file"
          accept="image/*"
          @change="onInputChange"
        />
      </div>
      <DialogFooter class="flex flex-row items-center justify-between">
        <DialogClose as-child>
          <Button type="button" variant="ghost">Cancel</Button>
        </DialogClose>
        <Button type="submit" @click="onSave">Save</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';

import { ImageCropper } from '@ui/components/image-cropper';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogFooter,
  DialogClose,
} from '@ui/components/dialog';
import { Button } from '@ui/components/button';

import SvgIcon from '@ui/components/SvgIcon.vue';
import requests from '@/vuex/requests.js';
import { queries } from '@/queries/index.js';
import { useStore } from 'vuex';

const src = ref(null);

const open = defineModel('open', { type: Boolean, default: false });

const cropper = ref(null);
const highlight = ref(false);

const queryClient = useQueryClient();

watch(open, newValue => {
  if (!newValue) {
    src.value = null;
  }
});

function handleFile(file) {
  src.value = URL.createObjectURL(file);
}

function onDragEnter() {
  highlight.value = true;
}

function onDragOver() {
  highlight.value = true;
}

function onDragLeave() {
  highlight.value = false;
}

function onDrop(e) {
  highlight.value = false;

  const dt = e.dataTransfer;
  const { files } = dt;

  handleFile(files[0]);
}

function onInputChange(e) {
  handleFile(e.target.files[0]);
}

const store = useStore();

function onSave() {
  cropper.value.toBase64().then(res => {
    requests.workspaces
      .changeIcon(store.getters['workspace/getId'], { image: res })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: queries.workspace.current.queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: queries.workspace.connections.queryKey,
        });
        open.value = false;
      });
  });
}
</script>
